import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = '/api/companies';

// Async Thunks
export const fetchCompanies = createAsyncThunk('companies/fetch', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get(API_URL);
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const createCompany = createAsyncThunk('companies/create', async (formData, { rejectWithValue }) => {
  try {
    // formData must be FormData object for file uploads
    const response = await axios.post(API_URL, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const updateCompany = createAsyncThunk('companies/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await axios.put(`${API_URL}/${id}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const deleteCompany = createAsyncThunk('companies/delete', async (id, { rejectWithValue }) => {
  try {
    await axios.delete(`${API_URL}/${id}`);
    return id;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

const companiesSlice = createSlice({
  name: 'companies',
  initialState: {
    items: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchCompanies.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.loading = false;
        // Safely handle if data is nested in data.data or just data, or missing
        state.items = action.payload?.data || [];
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message || 'Failed to fetch companies';
        state.items = []; // Ensure items is an array even on error
      })
      // Create
      .addCase(createCompany.fulfilled, (state, action) => {
        state.items.unshift(action.payload.data);
      })
      // Update
      .addCase(updateCompany.fulfilled, (state, action) => {
        const index = state.items.findIndex((item) => item.id === action.payload.data.id);
        if (index !== -1) {
          state.items[index] = action.payload.data;
        }
      })
      // Delete
      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.id !== action.payload);
      });
  },
});

export default companiesSlice.reducer;
